import React, { useState, useEffect } from "react";
import {
  Button,
  Space,
  Tag,
  Spin,
  Alert,
  Tooltip,
  Card,
  Typography,
  Descriptions,
  Divider,
  Row,
  Col,
  Badge,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  WarningOutlined,
  LoadingOutlined,
  FileTextOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { BlobProvider, PDFDownloadLink } from "@react-pdf/renderer";
import ActaPDF from "./Actapdf";

const { Title, Text } = Typography;

// ─── Visor iframe ─────────────────────────────────────────────────────────────
const PdfIframeViewer = ({ blob, nombreArchivo }) => {
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    setBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  if (!blobUrl) return null;

  return (
    <iframe
      src={blobUrl}
      title={nombreArchivo}
      width="100%"
      height="680px"
      style={{ border: "none", display: "block" }}
    />
  );
};

// ─── Componente Principal ─────────────────────────────────────────────────────
const PreviewActa = ({ participante, onVolver }) => {
  if (!participante) return null;

  const nombreArchivo = `acta_${participante.id_registro}_${participante.num_identificacion}.pdf`;
  const documento = <ActaPDF participante={participante} />;

  return (
    /*
      position: fixed + inset: 0 + zIndex: 1000
      → cubre TODA la ventana incluyendo el footer (z-index: 100)
      overflowY: auto → el scroll ocurre dentro de este panel, no en la página
    */
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1000,
        background: "#f5f5f5",
        overflowY: "auto",
        padding: "24px",
      }}
    >
      {/* ── Encabezado ──────────────────────────────────────────────────────── */}
      <Card
        style={{ marginBottom: 16, borderRadius: 8 }}
        bodyStyle={{ padding: "16px 24px" }}
      >
        <Row align="middle" justify="space-between" wrap={false}>
          <Col flex="auto">
            <Space size={16} align="center">
              <Tooltip title="Volver a la lista de participantes">
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={onVolver}
                  size="middle"
                >
                  Volver
                </Button>
              </Tooltip>

              <Divider type="vertical" style={{ height: 36, margin: 0 }} />

              <Space direction="vertical" size={0}>
                <Space size={8} align="center">
                  <FileTextOutlined style={{ color: "#1677ff", fontSize: 16 }} />
                  <Title level={5} style={{ margin: 0, fontSize: 15 }}>
                    Vista Previa del Acta
                  </Title>
                </Space>
                <Space size={6} style={{ marginTop: 2 }}>
                  <Tag color="blue" style={{ margin: 0 }}>
                    {participante.tipo_documento}
                  </Tag>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {participante.nombre_completo}
                  </Text>
                  <Text type="secondary" style={{ fontSize: 12 }}>·</Text>
                  <Text code style={{ fontSize: 11 }}>
                    {participante.id_registro}
                  </Text>
                </Space>
              </Space>
            </Space>
          </Col>

          <Col flex="none">
            <PDFDownloadLink
              document={documento}
              fileName={nombreArchivo}
              style={{ textDecoration: "none" }}
            >
              {({ loading }) => (
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  loading={loading}
                  disabled={loading}
                >
                  {loading ? "Generando..." : "Descargar PDF"}
                </Button>
              )}
            </PDFDownloadLink>
          </Col>
        </Row>
      </Card>

      {/* ── Datos del participante ───────────────────────────────────────────── */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Space>
                <IdcardOutlined style={{ color: "#1677ff" }} />
                <span>Identificación</span>
              </Space>
            }
            style={{ borderRadius: 8, height: "100%" }}
          >
            <Descriptions
              column={1}
              size="small"
              labelStyle={{ color: "#8c8c8c", width: 140 }}
            >
              <Descriptions.Item label="Nombre completo">
                <Text strong>{participante.nombre_completo}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Tipo documento">
                <Tag color="blue">{participante.tipo_documento}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="N° Identificación">
                <Text code>{participante.num_identificacion}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="N° Registro">
                <Text code>{participante.id_registro}</Text>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} md={12}>
          <Card
            size="small"
            title={
              <Space>
                <EnvironmentOutlined style={{ color: "#52c41a" }} />
                <span>Ubicación y Estado</span>
              </Space>
            }
            style={{ borderRadius: 8, height: "100%" }}
          >
            <Descriptions
              column={1}
              size="small"
              labelStyle={{ color: "#8c8c8c", width: 140 }}
            >
              <Descriptions.Item label="Departamento">
                {participante.departamento}
              </Descriptions.Item>
              <Descriptions.Item label="Municipio">
                {participante.municipio}
              </Descriptions.Item>
              <Descriptions.Item label="Estado documento">
                <Badge
                  status="warning"
                  text={
                    <Text style={{ color: "#d46b08", fontSize: 12 }}>
                      Pendiente de carga
                    </Text>
                  }
                />
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>

      {/* ── Alerta ──────────────────────────────────────────────────────────── */}
      <Alert
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
        message="Revisa el documento antes de proceder"
        description={`Este es el acta generada para ${participante.nombre_completo}. Verifica que los datos sean correctos antes de cargar el documento definitivo.`}
        style={{ borderRadius: 8, marginBottom: 16 }}
      />

      {/* ── Visor PDF ───────────────────────────────────────────────────────── */}
      <Card
        style={{ borderRadius: 8 }}
        bodyStyle={{ padding: 0 }}
        title={
          <Space>
            <FilePdfOutlined style={{ color: "#ff4d4f" }} />
            <Text style={{ fontSize: 13 }}>{nombreArchivo}</Text>
          </Space>
        }
        extra={
          <PDFDownloadLink
            document={documento}
            fileName={nombreArchivo}
            style={{ textDecoration: "none" }}
          >
            {({ loading }) => (
              <Button size="small" icon={<DownloadOutlined />} loading={loading} type="text">
                {loading ? "Generando..." : "Descargar"}
              </Button>
            )}
          </PDFDownloadLink>
        }
      >
        <BlobProvider document={documento}>
          {({ blob, loading, error }) => {
            if (error) {
              return (
                <div style={{ padding: 24 }}>
                  <Alert
                    type="error"
                    showIcon
                    icon={<WarningOutlined />}
                    message="Error al generar el PDF"
                    description={error.message}
                    style={{ borderRadius: 8 }}
                  />
                </div>
              );
            }

            if (loading || !blob) {
              return (
                <div
                  style={{
                    height: 500,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 16,
                  }}
                >
                  <Spin indicator={<LoadingOutlined style={{ fontSize: 36 }} spin />} />
                  <Text type="secondary">Generando vista previa del acta...</Text>
                </div>
              );
            }

            return <PdfIframeViewer blob={blob} nombreArchivo={nombreArchivo} />;
          }}
        </BlobProvider>
      </Card>

      {/* ── Footer con acciones ──────────────────────────────────────────────── */}
      <Card
        style={{ marginTop: 16, borderRadius: 8 }}
        bodyStyle={{ padding: "14px 24px" }}
      >
        <Row justify="space-between" align="middle">
          <Col>
            <Text type="secondary" style={{ fontSize: 12 }}>
              <InfoCircleOutlined style={{ marginRight: 6 }} />
              Verifica los datos antes de cargar el documento definitivo.
            </Text>
          </Col>
          <Col>
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={onVolver}>
                Volver a la lista
              </Button>
              <PDFDownloadLink
                document={documento}
                fileName={nombreArchivo}
                style={{ textDecoration: "none" }}
              >
                {({ loading }) => (
                  <Button type="primary" icon={<DownloadOutlined />} loading={loading}>
                    {loading ? "Generando PDF..." : "Descargar PDF"}
                  </Button>
                )}
              </PDFDownloadLink>
            </Space>
          </Col>
        </Row>
      </Card>

    </div>
  );
};

export default PreviewActa;